import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatDate, formatDateToTime } from "../../utils/dateTimeUtils";
import { useEffect, useState } from "react";
import { useLanguage } from "../../providers/preferenceProvider";
import { getStatistics, getStatisticsFilters } from "../../api/statistics-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import FiltersList from "../filters/filtersList";
import { useSearchParams } from "react-router-dom";

const Statistics = () => {
  const [errorStatistics, setErrorStatistics] = useState([]);
  const [bookingCreation, setBookingCreation] = useState([]);
  const [showErrorCharts, setShowErrorCharts] = useState(true);
  const [showBookingCharts, setShowBookingCharts] = useState(true);
  const { t } = useLanguage();

  const [filtersList, setFiltersList] = useState([]);
  const [clear] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Fetch statistics data from the API
    const fetchFilters = async () => {
      setFiltersList((await getStatisticsFilters()).data);
    };

    fetchFilters();
  }, []);

  useEffect(() => {
    fetchStatistics(searchParams);
  }, [searchParams])

  async function fetchStatistics(searchParams) {
    const params = new URLSearchParams();
    if (searchParams.get('filters')){
      appendQueryParamsFromSelected(searchParams.get('filters'), params);
    }
    const statistics = (await getStatistics(params)).data;
    setErrorStatistics(statistics.errorStatistics);
    setBookingCreation(statistics.bookingCreation);
  }

  function appendQueryParamsFromSelected(filters, queryParams) {
    const filterObject = JSON.parse(filters);
    for (const [name, items] of Object.entries(filterObject)) {
      items.forEach(item => {
        queryParams.append(name, item);
      });
    }
  }
  
  function selectedListChanged(filters){
    let filteredFilters = { ...filters };
    Object.keys(filters).filter(key => filters[key].length < 1).forEach(key => {
      delete filteredFilters[key];
    })

    mapFiltersToKeys(filteredFilters);

    searchParams.set('filters', JSON.stringify(filteredFilters));
    setSearchParams(searchParams);
  }
  
  function mapFiltersToKeys(filteredFilters) {
    Object.keys(filteredFilters).forEach(key => {
      filteredFilters[key] = filteredFilters[key].map(item => item.key);
    });
  }

  function formatDateUsingParams(date, params) {
    if (JSON.parse(params.get('filters'))?.range?.[0] === 'month' ){
      return formatDate(new Date(date).toISOString());
    } else {
      return formatDateToTime(new Date(date).toISOString());
    }
  }
  
  return (
    <>
      <div className="filters mt-4 flex flex-wrap text-sm">
        <FiltersList listFilters={filtersList} onSelectedChange={x => selectedListChanged(x)} clear={clear}/>
      </div>
      <div className="mb-4">
        <button
          onClick={() => setShowErrorCharts((prev) => !prev)}
          className="w-full flex items-center gap-2 px-4 py-2 bg-gray-300 rounded-[7px] mt-4"
        >
          <span>Errors</span>
          <span><FontAwesomeIcon icon={showErrorCharts ? faChevronDown : faChevronUp}/></span>
        </button>

        {showErrorCharts && (
          <div className="mr-4">
            <div className="w-full h-[15rem]">
              <ResponsiveContainer className="w-full mt-4">
                <LineChart
                  data={errorStatistics}
                  margin={{
                    right: 32,
                    top: 16,
                    left: 8,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tickFormatter={(tick) => formatDateUsingParams(tick, searchParams)} label={{ value: 'Time', position: 'insideBottomRight', offset: -16 }}/>
                  <YAxis dataKey="error" name="Errors"/>
                  <Tooltip labelFormatter={(label) => formatDateUsingParams(label, searchParams)}/>
                  <Legend />
                  <Line type="monotone" dataKey="error" stroke="#232F44" activeDot={{ r: 8 }} name='Errors' />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
      <div className="mb-4">
        <button
          onClick={() => setShowBookingCharts((prev) => !prev)}
          className="w-full flex items-center gap-2 px-4 py-2 bg-gray-300 rounded-[7px] mt-4"
        >
          <span>{t.bookings}</span>
          <span><FontAwesomeIcon icon={showBookingCharts ? faChevronDown : faChevronUp}/></span>
        </button>

        {showBookingCharts && (
          <div className="mr-4">
            <div className="w-full h-[15rem]">
              <ResponsiveContainer className="w-full mt-4 max-h-[15rem]">
                <LineChart
                  height={300}
                  data={bookingCreation}
                  margin={{
                    right: 32,
                    top: 16,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tickFormatter={(tick) => formatDateUsingParams(tick, searchParams)} label={{ value: 'Time', position: 'insideBottomRight', offset: -16 }}/>
                  <YAxis dataKey="booking" name="Bookings"/>
                  <Tooltip labelFormatter={(label) => formatDateUsingParams(label, searchParams)}/>
                  <Legend />
                  <Line type="monotone" dataKey="booking" stroke="#232F44" activeDot={{ r: 8 }} name='Bookings' />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Statistics;
